/**
 * Surveillance map layers — mockup parity with `_drawFusedSurvSensors`,
 * `_drawSurvSensorMarkers`, `drawCoverageLayer` (flysafe_mockup_179_54.html
 * §setSurvMode, §_drawFusedSurvSensors L9794–9831, §setSurvNetwork L9833).
 *
 * Fused mode  (`surv.mode === 'fused'`):
 *   - Sensor pins for EVERY service, coloured by service (the "all overlay").
 *   - State (live/degraded/offline) tweaks fill opacity + falls back to
 *     amber/grey for non-live, matching mockup L9810–9813.
 *   - Coverage circles are NOT drawn in fused.
 *
 * Diagnostic mode  (`surv.mode === 'diagnostic'`):
 *   - Sensor pins for `selectedNetwork` only (default Live_MLAT — set by
 *     setMode in store).
 *   - Coverage circles drawn around live stations of that service.
 *
 * Service colour key matches mockup `SURV_SERVICE_COLOURS` (L9786):
 *   MLAT      → blue   (#2563eb)
 *   ADSB      → green  (#16a34a)
 *   Vision    → purple (#a855f7)
 *   Community → amber  (#f59e0b)
 *   Drone_RF  → red    (#e11d48)
 */
import mapboxgl from 'mapbox-gl';
import { markFramed, shouldAutoFit } from '@/lib/map/cameraMemory';
import type { GeoJSONSource, LngLatBoundsLike, Map as MapboxMap } from 'mapbox-gl';
import type { Feature, Polygon } from 'geojson';
import { useSurvStore } from '@/lib/store/surv';
import { useUiStore } from '@/lib/store/ui';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/**
 * Sensor pin tooltip — compact light-theme card shown on hover. Mirrors
 * the mockup's Leaflet `bindTooltip` look (`sticky: true, direction: top`):
 * small white box, monospace text, subtle shadow, no close button.
 *
 *   fused mode (mockup `_drawFusedSurvSensors` L9815-9820)
 *     ┌─────────────────────┐
 *     │ {id}                 │
 *     │ {service_id}         │  ← coloured in service hex
 *     │ {label}              │
 *     └─────────────────────┘
 *
 *   diagnostic mode (mockup `_drawSurvSensorMarkers` L9872-9875)
 *     ┌─────────────────────────────┐
 *     │ {id}                         │
 *     │ {label} · {stats}            │
 *     └─────────────────────────────┘
 *
 * `mode`, `service`, and `color` are stamped onto each GeoJSON feature
 * by `syncSurveillanceLayers`, so the popup picks its shape from the
 * feature alone — no extra store lookup. */
function buildSensorPopupHtml(props: Record<string, unknown>): string {
  const id = String(props.sensor_id ?? '—');
  const label = String(props.label ?? '');
  const mode = (props.mode === 'diagnostic' ? 'diagnostic' : 'fused') as 'fused' | 'diagnostic';
  const service = props.service ? String(props.service) : '';
  const serviceColor = props.color ? String(props.color) : '#6b7280';
  const latency = props.latency_ms as number | null | undefined;
  const rtt = props.rtt_ms as number | null | undefined;
  const rssi = props.rssi as number | null | undefined;
  const targets = props.target_count as number | undefined;

  // Card shell — compact, white, mono font (Leaflet-tooltip parity).
  const open =
    `<div style="font-family:IBM Plex Mono,monospace;font-size:11px;line-height:1.45;color:#1f2937;` +
    `min-width:140px;max-width:240px">`;
  const close = `</div>`;

  if (mode === 'fused') {
    const serviceLine = service
      ? `<div style="font-weight:600;color:${serviceColor};margin-top:2px">${escapeHtml(service)}</div>`
      : '';
    const labelLine = label
      ? `<div style="color:#555;margin-top:1px">${escapeHtml(label)}</div>`
      : '';
    return open +
      `<div style="font-weight:700">${escapeHtml(id)}</div>` +
      serviceLine +
      labelLine +
      close;
  }

  // Diagnostic — service is already implied by the sidebar selection.
  // Surface metrics inline: RTT (vision only), latency, RSSI, peer count.
  const stats: string[] = [];
  if (rtt != null) stats.push(`RTT ${Math.round(rtt)} ms`);
  if (latency != null) stats.push(`${Math.round(latency)} ms`);
  if (rssi != null) stats.push(`${rssi} dBm`);
  if (targets != null) stats.push(`${targets} tgt`);
  const statsStr = stats.join(' · ');
  const labelStats = [label, statsStr].filter(Boolean).join(' · ');
  return open +
    `<div style="font-weight:700">${escapeHtml(id)}</div>` +
    (labelStats ? `<div style="color:#555;margin-top:1px">${escapeHtml(labelStats)}</div>` : '') +
    close;
}

/** Coverage polygon tooltip — same compact light card as sensor pins.
 *  Mirrors mockup `poly.bindTooltip` (L10983):
 *    "{agl} ft AGL · {N} sensors · {Accurate|Not Accurate}". */
function buildCoveragePopupHtml(props: Record<string, unknown>): string {
  const agl = Number(props.agl ?? 0);
  const sensors = Number(props.sensors_count ?? 0);
  const accurate = props.accurate === true;
  const tone = accurate ? '#22a050' : '#ef4444';
  const label = accurate ? 'Accurate' : 'Not Accurate';
  return (
    `<div style="font-family:IBM Plex Mono,monospace;font-size:11px;line-height:1.45;` +
    `color:#1f2937;min-width:160px;max-width:240px">` +
      `<div style="font-weight:700">${agl.toLocaleString()} ft AGL</div>` +
      `<div style="color:#555;margin-top:1px">${sensors} sensor${sensors === 1 ? '' : 's'} · ` +
        `<span style="color:${tone};font-weight:600">${label}</span></div>` +
    `</div>`
  );
}

const SRC_SENSORS = 'src-surv-sensors';
const SRC_COVERAGE = 'src-surv-coverage';

// Service colour key — ticket 86e1fvf06 (7 services, lowercase ids).
// Stays in lockstep with SurvList.SERVICE_COLOR and
// markers/targets.SURV_SERVICE_TINT.
export const SERVICE_COLOR: Record<string, string> = {
  live_ads:                     '#16a34a', // ADS-B green
  live_vision:                  '#a855f7', // purple
  live_mlat:                    '#2563eb', // blue
  live_drone_rf:                '#e11d48', // rose/red (non-cooperative)
  live_regional_calgary:        '#16a34a', // ADS-B green family
  live_regional_edmonton:       '#15803d', // ADS-B green family (darker)
  live_regional_fort_mcmurray:  '#22c55e', // ADS-B green family (lighter)
  live_fused:                   '#0d7f6a', // teal — fused output
};

/** Colour for any service id. The curated ones have their own; a service the
 *  broker reports that nobody has coloured yet gets the ADS-B green family when
 *  it is a regional aggregator (like the three that do), grey otherwise. */
export function serviceColor(id: string): string {
  return SERVICE_COLOR[id] ?? (id.startsWith('live_regional_') ? '#16a34a' : '#6b7280');
}

export function ensureSurveillanceLayers(map: MapboxMap) {
  if (!map.getSource(SRC_COVERAGE)) {
    map.addSource(SRC_COVERAGE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer('lyr-surv-coverage-fill')) {
    // Mockup `_drawCovPredPolygon` L10978-10981:
    //   colour = accurate (all sensors online) ? green (#22a050) : red (#ef4444)
    //   weight 2, opacity 0.85, fill opacity 0.08
    //   dashArray '6 4' only when NOT accurate
    map.addLayer({
      id: 'lyr-surv-coverage-fill',
      type: 'fill',
      source: SRC_COVERAGE,
      paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.08 },
    });
  }
  if (!map.getLayer('lyr-surv-coverage-line')) {
    map.addLayer({
      id: 'lyr-surv-coverage-line',
      type: 'line',
      source: SRC_COVERAGE,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': 0.85,
        // Solid when accurate, dashed [6,4] when not — encoded per-feature.
        'line-dasharray': ['case', ['get', 'dashed'], ['literal', [6, 4]], ['literal', [1, 0]]],
      },
    });
  }
  if (!map.getSource(SRC_SENSORS)) {
    map.addSource(SRC_SENSORS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer('lyr-surv-sensors-bg')) {
    map.addLayer({
      id: 'lyr-surv-sensors-bg',
      type: 'circle',
      source: SRC_SENSORS,
      paint: {
        // Pin colour rules (deviation from mockup §1.2 —
        // "service-identity wins"):
        //
        // The mockup repaints degraded sensors amber (`#f59e0b`) and offline
        // gray, which has two operational problems:
        //   1. `live_Community`'s identity hex IS `#f59e0b` — when a
        //      Community sensor degrades you can't tell it from a degraded
        //      sensor of any other service.
        //   2. When ADSB or MLAT degrade (which they do every 30-90s on v1
        //      cadence), every pin loses its service identity at once —
        //      operator can't see which network is having trouble at a glance.
        //
        // New rule: identity colour wins in BOTH modes (fused = service hex,
        // diagnostic = plain green). State is encoded purely by opacity +
        // stroke pattern — a healthy ADSB pin reads as solid green, a degraded
        // one as faded green with an amber dashed outline, offline as faded
        // green with a gray dotted outline. Identity stays legible.
        'circle-color': [
          'case',
          ['==', ['get', 'mode'], 'diagnostic'], '#22c55e',
          ['get', 'color'],
        ],
        // Mockup: fused 8 px / diagnostic 9 px (`_drawFusedSurvSensors` L9815)
        'circle-radius': ['case', ['==', ['get', 'mode'], 'fused'], 8, 9],
        'circle-stroke-width': [
          'case',
          ['==', ['get', 'state'], 'live'], 2,
          /* degraded / offline get a thicker outline so state reads at a glance */ 3,
        ],
        // Stroke encodes state:
        //   live      → service-colour outline (or diagnostic dark green)
        //   degraded  → yellow outline  (`#facc15`, Tailwind yellow-400)
        //                — chosen because Community's identity hex (`#f59e0b`)
        //                is in the amber family, and any amber-family stroke
        //                (`#b45309` etc.) reads as "same colour" on a Community
        //                fill. Yellow-400 sits outside all five service
        //                identity hues so a degraded ring pops on every pin
        //                regardless of service.
        //   offline   → gray outline    (= dead, still service-coloured fill)
        'circle-stroke-color': [
          'case',
          ['==', ['get', 'state'], 'offline'], '#6b7280',
          ['==', ['get', 'state'], 'degraded'], '#facc15',
          ['==', ['get', 'mode'], 'diagnostic'], '#1d7a40',
          ['get', 'color'],
        ],
        // Opacity encodes state intensity: live = full, degraded = dimmed,
        // offline = ghost. Identity hue is preserved across all three.
        'circle-opacity': [
          'case',
          ['==', ['get', 'state'], 'offline'], 0.35,
          ['==', ['get', 'state'], 'degraded'], 0.55,
          /* live */ 0.85,
        ],
        'circle-stroke-opacity': [
          'case',
          ['==', ['get', 'state'], 'offline'], 0.6,
          ['==', ['get', 'state'], 'degraded'], 0.85,
          /* live */ 0.9,
        ],
      },
    });
  }
  if (!map.getLayer('lyr-surv-sensors-labels')) {
    map.addLayer({
      id: 'lyr-surv-sensors-labels',
      type: 'symbol',
      source: SRC_SENSORS,
      minzoom: 11,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 9,
        'text-offset': [0, 1.4],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: { 'text-color': '#1f2937', 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
    });
  }
}

export function syncSurveillanceLayers(map: MapboxMap) {
  // Note: we deliberately do NOT gate on `map.isStyleLoaded()` here.
  // setData() on an already-added GeoJSON source works regardless of
  // whether basemap tiles have finished loading — and on slow tile
  // fetches `isStyleLoaded()` can stay false for tens of seconds after
  // a fitBounds, which used to leave the source stale that whole time.
  // Source existence is the right gate: if `ensureSurveillanceLayers`
  // hasn't run yet (style.load not fired), `getSource` returns null
  // and we bail. The next attachCore call will retry.
  const sensorsSrc = map.getSource(SRC_SENSORS) as GeoJSONSource | undefined;
  const coverageSrc = map.getSource(SRC_COVERAGE) as GeoJSONSource | undefined;
  if (!sensorsSrc || !coverageSrc) return;

  const ui = useUiStore.getState();
  const surv = useSurvStore.getState();

  // Only render in surv-ctx. Clear otherwise.
  if (ui.activeContext !== 'surv') {
    sensorsSrc.setData({ type: 'FeatureCollection', features: [] });
    coverageSrc.setData({ type: 'FeatureCollection', features: [] });
    return;
  }

  const services = Object.values(surv.byId);
  // Live_Fused is not in `byId` — it is derived and held alongside. Include it
  // ONLY for the diagnostic branch: in fused mode its stations are the union of
  // the services already being drawn, so adding it would double every pin.
  const diagServices = surv.fusedService ? [...services, surv.fusedService] : services;
  const sensorFeatures: Feature[] = [];
  const coverageFeatures: Feature<Polygon>[] = [];

  if (surv.mode === 'fused') {
    // Fused = every service's sensors, in each service's colour.
    for (const svc of services) {
      const color = serviceColor(svc.service_id);
      for (const st of svc.stations) {
        sensorFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [st.lng, st.lat] },
          properties: {
            sensor_id: st.sensor_id,
            label: st.label,
            state: st.state,
            color,
            service: svc.service_id,
            mode: 'fused',
            latency_ms: st.latency_ms,
            rtt_ms: st.rtt_ms,
            rssi: st.rssi,
            target_count: st.target_count,
          },
        });
      }
    }
    // Fused mode does not draw coverage polygons (mockup behaviour).
  } else {
    // Diagnostic = only selected service's sensors + a single
    // coverage POLYGON for `activeCovModelIdx`.
    for (const svc of diagServices) {
      if (surv.selectedNetwork && svc.service_id !== surv.selectedNetwork) continue;
      const color = serviceColor(svc.service_id);
      for (const st of svc.stations) {
        sensorFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [st.lng, st.lat] },
          properties: {
            sensor_id: st.sensor_id,
            label: st.label,
            state: st.state,
            color,
            service: svc.service_id,
            mode: 'diagnostic',
            latency_ms: st.latency_ms,
            rtt_ms: st.rtt_ms,
            rssi: st.rssi,
            target_count: st.target_count,
          },
        });
      }
      // Coverage polygon from the active model (mockup `_drawCovPredPolygon`).
      // Colour is ACCURACY-based, not service-based: green when every
      // contributing sensor is `live`, red dashed when one or more are
      // degraded/offline (mockup L10978-10981).
      const idx = surv.activeCovModelIdx;
      if (idx != null && svc.coverageModels[idx]) {
        const model = svc.coverageModels[idx];
        if (model.polygon.length >= 3) {
          const onlineIds = new Set(svc.stations.filter((s) => s.state === 'live').map((s) => s.sensor_id));
          const accurate = model.sensors.every((sid) => onlineIds.has(sid));
          const polyColor = accurate ? '#22a050' : '#ef4444';
          const ring = [...model.polygon, model.polygon[0]!] as [number, number][];
          coverageFeatures.push({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [ring] },
            properties: {
              color: polyColor,
              dashed: !accurate,
              service: svc.service_id,
              agl: model.agl,
              accurate,
              sensors_count: model.sensors.length,
            },
          });
        }
      }
    }
  }

  sensorsSrc.setData({ type: 'FeatureCollection', features: sensorFeatures });
  coverageSrc.setData({ type: 'FeatureCollection', features: coverageFeatures });
}

/**
 * Auto-fit the map to the relevant sensor/coverage area when the surveillance
 * selection changes (mockup `_drawFusedSurvSensors` L9827 fitBounds, and
 * `selectSurvNet` → `_drawCovPredPolygon` L10946 polygon focus).
 *
 *   surv-ctx + fused                 → fit to ALL sensor positions
 *   surv-ctx + diagnostic + network  → fit to active coverage polygon
 *                                      (falls back to that service's sensors
 *                                       when there is no polygon yet)
 *   other ctx                        → no-op
 */
function autoFitSurv(map: MapboxMap) {
  const ui = useUiStore.getState();
  const surv = useSurvStore.getState();
  if (ui.activeContext !== 'surv') return;
  if (!map.isStyleLoaded()) return;

  let bounds: [[number, number], [number, number]] | null = null;
  const update = (lng: number, lat: number) => {
    if (!bounds) { bounds = [[lng, lat], [lng, lat]]; return; }
    if (lng < bounds[0][0]) bounds[0][0] = lng;
    if (lng > bounds[1][0]) bounds[1][0] = lng;
    if (lat < bounds[0][1]) bounds[0][1] = lat;
    if (lat > bounds[1][1]) bounds[1][1] = lat;
  };

  if (surv.mode === 'fused') {
    // Ticket 86e1fvf06 — live_community is filtered out at the parser
    // boundary, so the old "exclude globally-scattered Community feeds
    // from the bbox" guard is no longer needed: byId only contains the
    // 7 allowlist services.
    for (const svc of Object.values(surv.byId)) {
      for (const st of svc.stations) update(st.lng, st.lat);
    }
  } else if (surv.selectedNetwork) {
    const svc =
      surv.byId[surv.selectedNetwork] ??
      (surv.fusedService?.service_id === surv.selectedNetwork ? surv.fusedService : undefined);
    if (svc) {
      const idx = surv.activeCovModelIdx;
      const model = idx != null ? svc.coverageModels[idx] : null;
      if (model && model.polygon.length >= 3) {
        for (const [lng, lat] of model.polygon) update(lng, lat);
      } else {
        for (const st of svc.stations) update(st.lng, st.lat);
      }
    }
  }

  if (!bounds) return;
  markFramed('surv');
  const same = bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1];
  if (same) {
    map.easeTo({ center: bounds[0], zoom: Math.max(map.getZoom(), 11), duration: 600 });
  } else {
    map.fitBounds(bounds as LngLatBoundsLike, { padding: 60, maxZoom: 11, duration: 600 });
  }
}

export function subscribeSurveillanceLayers(map: MapboxMap): () => void {
  ensureSurveillanceLayers(map);
  let rafId: number | null = null;
  /** Set when a sync attempt found `!map.isStyleLoaded()` and bailed.
   *  The next `idle` event drains this and re-syncs once Mapbox settles
   *  — addLayer/addSource briefly invalidate `isStyleLoaded()`, which was
   *  causing the very first sync (right after `ensureSurveillanceLayers`)
   *  to silently no-op. Without the idle retry, fused-mode pins didn't
   *  appear until the user toggled diagnostic + back. */
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncSurveillanceLayers(map);
    });
  };
  // No `idle` retry. Each `setData` causes Mapbox to repaint, which
  // triggers another `idle` event — if `onIdle` called sync, we'd
  // recurse at the browser's frame rate. Source-readiness is handled
  // by `attachCore` running on `style.load` (which calls
  // `ensureSurveillanceLayers` to re-add sources after basemap swap);
  // every other retrigger comes from store subscriptions below.
  syncSurveillanceLayers(map);
  const unsubMode = useSurvStore.subscribe(
    (s) => `${s.mode}|${s.selectedNetwork}|${s.activeCovModelIdx}|${Object.keys(s.byId).length}`,
    schedule,
  );
  const unsubById = useSurvStore.subscribe((s) => s.byId, schedule);
  // Starting / stopping a fusion changes only `fusedService`, so `byId` alone
  // would leave the map stale until the next envelope.
  const unsubFused = useSurvStore.subscribe((s) => s.fusedService, schedule);
  const unsubCtx = useUiStore.subscribe((s) => s.activeContext, schedule);

  // Auto-fit on the same triggers, but with a small debounce so the user's
  // cov-model click → store update → fitBounds chain feels intentional.
  let fitId: number | null = null;
  const scheduleFit = () => {
    if (fitId !== null) clearTimeout(fitId);
    fitId = window.setTimeout(() => { fitId = null; autoFitSurv(map); }, 80);
  };
  const unsubFitSurv = useSurvStore.subscribe(
    (s) => `${s.mode}|${s.selectedNetwork}|${s.activeCovModelIdx}`,
    scheduleFit,
  );
  // Auto-fit on ENTERING surv, but only on the first visit and only while the
  // user hasn't positioned the map themselves — a tab switch must never move
  // the shared camera. The selection-driven fits above are unaffected; those
  // are the user asking for a new frame.
  const unsubFitCtx = useUiStore.subscribe(
    (s) => s.activeContext,
    (ctx) => { if (ctx === 'surv' && shouldAutoFit('surv')) scheduleFit(); },
  );

  // Mockup `marker.bindTooltip` (L9872) + `poly.bindTooltip` (L10983) use
  // Leaflet's sticky-hover tooltip behaviour: a small card appears while
  // the cursor is over the feature, follows it as it moves, and vanishes
  // on mouseleave. Match that with mapboxgl.Popup driven by mouse events.
  //
  // No close button, no closeOnClick — those belong to click-popups, which
  // would force the user to tap pins (no parity with aircraft hover).
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    closeOnMove: false,
    maxWidth: '260px',
    offset: 10,
    className: 'flysafe-surv-popup',
  });

  // Sensor pins sit INSIDE coverage polygons most of the time, so a naive
  // dual-handler setup would race: coverage's mousemove would overwrite
  // sensor's popup on every cursor jitter. Priority is "sensor wins" —
  // sensor handler runs unconditionally, coverage handler yields when a
  // sensor is at the same screen point.
  const onSensorMove = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
    const f = e.features?.[0];
    if (!f) return;
    map.getCanvas().style.cursor = 'pointer';
    popup
      .setLngLat(e.lngLat)
      .setHTML(buildSensorPopupHtml((f.properties ?? {}) as Record<string, unknown>))
      .addTo(map);
  };
  const onCoverageMove = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
    // Yield to sensor when both layers are under the cursor. Without this
    // check, hovering a pin inside its own service's coverage polygon
    // would show the polygon's tooltip — masking the sensor detail.
    const sensors = map.queryRenderedFeatures(e.point, { layers: ['lyr-surv-sensors-bg'] });
    if (sensors.length > 0) return;
    const f = e.features?.[0];
    if (!f) return;
    map.getCanvas().style.cursor = 'pointer';
    popup
      .setLngLat(e.lngLat)
      .setHTML(buildCoveragePopupHtml((f.properties ?? {}) as Record<string, unknown>))
      .addTo(map);
  };

  // Per-layer leave handlers. Sensor leave re-checks if cursor is still
  // over a coverage polygon and switches to that popup instead of
  // clearing — avoids a one-frame flicker as the user slides off a pin
  // but stays inside its polygon. Coverage leave checks for sensor
  // (rare path: cursor leaves polygon onto a pin outside polygon area).
  const onSensorLeave = (e: mapboxgl.MapMouseEvent) => {
    const coverage = map.queryRenderedFeatures(e.point, { layers: ['lyr-surv-coverage-fill'] });
    if (coverage.length > 0) {
      map.getCanvas().style.cursor = 'pointer';
      popup
        .setLngLat(e.lngLat)
        .setHTML(buildCoveragePopupHtml((coverage[0]!.properties ?? {}) as Record<string, unknown>))
        .addTo(map);
      return;
    }
    map.getCanvas().style.cursor = '';
    popup.remove();
  };
  const onCoverageLeave = (e: mapboxgl.MapMouseEvent) => {
    const sensors = map.queryRenderedFeatures(e.point, { layers: ['lyr-surv-sensors-bg'] });
    if (sensors.length > 0) return;        // sensor handler owns the popup
    map.getCanvas().style.cursor = '';
    popup.remove();
  };

  // `mouseenter` fires once when the cursor enters the feature; `mousemove`
  // keeps the popup attached to the cursor (mockup `sticky: true`).
  map.on('mouseenter', 'lyr-surv-sensors-bg', onSensorMove);
  map.on('mousemove',  'lyr-surv-sensors-bg', onSensorMove);
  map.on('mouseleave', 'lyr-surv-sensors-bg', onSensorLeave);
  map.on('mouseenter', 'lyr-surv-coverage-fill', onCoverageMove);
  map.on('mousemove',  'lyr-surv-coverage-fill', onCoverageMove);
  map.on('mouseleave', 'lyr-surv-coverage-fill', onCoverageLeave);

  return () => {
    unsubMode();
    unsubById();
    unsubFused();
    unsubCtx();
    unsubFitSurv();
    unsubFitCtx();
    if (fitId !== null) clearTimeout(fitId);
    map.off('mouseenter', 'lyr-surv-sensors-bg', onSensorMove);
    map.off('mousemove',  'lyr-surv-sensors-bg', onSensorMove);
    map.off('mouseleave', 'lyr-surv-sensors-bg', onSensorLeave);
    map.off('mouseenter', 'lyr-surv-coverage-fill', onCoverageMove);
    map.off('mousemove',  'lyr-surv-coverage-fill', onCoverageMove);
    map.off('mouseleave', 'lyr-surv-coverage-fill', onCoverageLeave);
    popup.remove();
  };
}
